// hook dependancies
import { useState, useEffect, useGlobal, setGlobal } from "reactn";
import basketIcon from "../images/basket.svg";
import searchIcon from "../images/search.svg";

/**
 * hook to get countries from the API
 * this is used to populate the drop down list of destinations for step one
 */
const AppHook = (validate, callback, config) => {
    /**
     * Used to set and handle global error state
     */
    const [globalError] = useGlobal("globalError");
    const [sideNavIndex, setSideNavIndex] = useGlobal("sideNavIndex");
    const [isBusiness, setIsBusiness] = useGlobal("isBusiness");
    const [mobileNav, setMobileNav] = useGlobal("mobileNav");
    const [topNav, setTopNav] = useGlobal("topNav");

    const handleCloseError = () => {
        // Set the reason for return to be used on the contact details screen
        setGlobal({
            globalError: !globalError
        });
    };

    /**
     * `values` stores an object of form values. It gets populated on submit and keyup.
     * `setValues` is a function used to populate the `values` object.
     * `values` is used for Form validation and passing values to the api.
     */
    const [values] = useGlobal("values");

    /**
     * `errors` stores form error messages.
     * `setErrors` is used with `validate` to populated the errors object e.g `setErrors(validate(values));`
     */
    const [errors, setErrors] = useState({});

    /**
     * `focus` used with onblur and obfocus to store the currently focued form values
     * `focus` is returened and imported in RegistrationForm to check and
     * set if focus is true, we used this to handle label animations.
     */
    const [focus, setFocus] = useState({});

    /**
     * `isSubmitting` set to false by default. When form is submitting this is set to `true`
     * it is used to detect if the submit button has been pressed. OnkeyUp this is set to
     * false again so the form doesnt submit on keyup.
     */
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * `isValidating` set to true on keyup so we can validate on the fly without doing an api call by
     * setting isSubmitting to true.
     */
    const [isValidating, setIsValidating] = useState(false);

    /**
     * `hasErrors` set to true if the current form has errors
     * once has errors is set to false then the submit button will be enabled
     */
    const [hasErrors, setHasErrors] = useState(false);

    /**
     * `isLoading` is set to false by default. On submit this is set to `true` and then set to `false`
     * again when the api has a response. Used on the submit button to disable the button if `true` and
     * enable it again if `false`.
     */
    const [isLoading, setIsLoading] = useState(false);

    /**
     * useEffect replaces the `componentDidMount` and `componentDidUpdate` lifecycle methods in React Class components.
     * This is were we handle the form submission. We pass [errors] as the second parameter for useEffect so we can run
     * it every time `[errors] changes.
     */
    useEffect(() => {
        // check if there are no errors and the submit button has been pressed
        if (Object.keys(errors).length === 0 && isSubmitting) {
            if (callback) callback();
            // do a post to the registration api and pass the form values

            // reset submitting to false to prevent duplicate API calls
            setIsSubmitting(false);
        } else if (Object.keys(errors).length > 0 && isSubmitting) {
            // scroll to the error
            const errorMsg = document.getElementById(Object.keys(errors)[0]);

            if (!errorMsg) return;

            // scroll into view of error on submit
            errorMsg.scrollIntoView({ block: "center", behavior: "smooth" });
        }
    }, [errors, isSubmitting, values]);

    /**
     * Handles form submit
     */
    const handleSubmit = event => {
        if (event) event.preventDefault();
        // validates form fields and sets errors.
        setErrors(validate(values, event, config));
        setIsValidating(true);
        setIsSubmitting(true);
        if (Object.keys(errors).length === 0 && isSubmitting) {
            callback();
        }
    };

    const handleProceedToPayment = (event, paymentCallback) => {
        if (event) event.preventDefault();
        event.target.classList.add("bn--disabled");

        // validates form fields and sets errors.
        if (Object.keys(validate(values, event, config)).length === 0) {
            setIsValidating(true);
            setIsSubmitting(true);
            paymentCallback(event);
        } else {
            setErrors(validate(values, event, config));
            event.target.classList.remove("bn--disabled");
        }
    };
    /**
     * Check if an object has an entry
     */
    const isObjectEmpty = obj => {
        return Object.getOwnPropertyNames(obj).length < 1;
    };

    /**
     * Handles input change events
     */
    const handleChange = event => {
        event.persist();
        // sets `values` object which is used for validation and form submission
        setGlobal({
            values: { ...values, [event.target.name]: event.target.value.replace(/^\s+/g, "") },
        });


        // validate errors
        if (isObjectEmpty(validate(values, event, config))) {
            setHasErrors(true);
        } else {
            setHasErrors(false);
        }
    };
    /**
     * Handles input change events
     */
    const handleChangeCheckbox = event => {
        event.persist();
        // sets `values` object which is used for validation and form submission

        setGlobal({
            values: { ...values, [event.target.name]: event.target.checked }
        });

        // validate errors
        if (isObjectEmpty(validate(values, event, config))) {
            setHasErrors(true);
        } else {
            setHasErrors(false);
        }
    };

    /**
     * Hand Radio change
     */
    const handleChangeRadio = event => {
        event.persist();
        // sets `values` object which is used for validation and form submission

        setGlobal({
            values: { ...values, [event.target.name]: event.target.value }
        });
    };

    /**
     * Handles input change events on phone number fields
     */
    const handleChangePhone = event => {
        event.preventDefault();
        errors[event.target.name] =
            /\D/.test(event.target.value.replace(/[+\d\s]/g, "")) || (event.target.value.length >= 5 && !/^[+\d\s]{5,12}$/.test(event.target.value))
                ? "Please enter a valid phone number (5-12 digits)"
                : null;
        handleChange(event);
    };

    /**
     * Handles input change events on first and last name fields
     */
    const handleChangeName = event => {
        event.preventDefault();
        errors[event.target.name] = !/^([a-zÀ-ž\u00C0-\u02AB-\u0370-\u03FF\u0400-\u04FF´`-‘’']\.?\s?)+$/i.test(event.target.value) ? "Please enter a valid name" : null;
        handleChange(event);
    };

    /**
     * Set individual item in the values object
     */
     const setIndividualValue = (name, newVal) => {
        setGlobal({
            values: { ...values, [name]: newVal }
        });
    };

    /**
     * Handles input change events when digits are now allowed
     */
    const handleChangeNoDigits = event => {
        event.preventDefault();
        const eventObj = event;
        if (/\d/.test(eventObj.target.value)) {
            errors[eventObj.target.name] = "No digits allowed";
            eventObj.target.value = values[eventObj.target.name] || null;
        } else {
            errors[eventObj.target.name] = null;
        }
        handleChange(eventObj);
    };

    const checkNavLink = url => {
        if (url.toLowerCase().includes("myaccount")) {
            return true;
        }

        if (url.toLowerCase().includes("click-and-post")) {
            return true;
        }

        if (url.toLowerCase().includes("post-parcels")) {
            return true;
        }

        if (url.toLowerCase().includes("shop")) {
            return true;
        }

        if (url.toLowerCase().includes("money")) {
            return true;
        }

        return false;
    };

    const setActiveLink = link => {
        link.classList.remove("active");
        
        if (window.location.pathname.replace(/(\/Business)(.*)/, "").endsWith(link.href.split("/").pop())) {
            link.classList.add("active");
        }
    }

    const setNavProfileLinks = (dropdownLinks, history) => {
        dropdownLinks.forEach(link => {
            link.addEventListener("click", e => {
                const clickedEl = e.currentTarget;
                const clickedLinkHref = clickedEl.href;
        
                // if external link
                if (checkNavLink(clickedLinkHref)) return;
        
                e.preventDefault();

                if (clickedEl.hasAttribute("data-m59-logout")) {
                    window.apgigya.logout({ callback: window.m59.logout });
                    return;
                };

                let clickedLinkPage = clickedLinkHref.split("/").pop();
        
                clickedLinkPage.includes("Transactions") ? setSideNavIndex(2) : setSideNavIndex(0);
                clickedLinkPage.toLowerCase().includes("my-deliveries") && (clickedLinkPage = "/");
        
                // if user is already on my account page remove the myAccount string from the link
                const link = () => {
                    if (window.location.href.endsWith("/My-Deliveries") && isBusiness) {
                        return `${clickedLinkPage}/Business`;
                    }
                    if (clickedLinkPage.includes("My-Deliveries")) {
                        return "/";
                    }
        
                    return clickedLinkPage;
                };
        
                history.push(link());
            });

            setActiveLink(link);
        });
    };

    const handleMainMargin = (collapsed) => {
        const mainSection = document.querySelector("#main");
        const footer = document.querySelector(".footer");
        if (collapsed) {
            mainSection.style.marginLeft = "11.2rem";
            footer.style.marginLeft = "11.2rem";
        } else {
            mainSection.style.marginLeft = "29.3rem";
            footer.style.marginLeft = "29.3rem";
        }
    };

    const removeNavMargins = () => {
        const mainSection = document.querySelector("#main");
        const footer = document.querySelector(".footer");
        const gridContainer = footer.querySelectorAll(".grid-container");
        const announcementBanner = document.querySelector(".m02");
        announcementBanner && (announcementBanner.removeAttribute("style"));
        mainSection && (mainSection.removeAttribute("style"));
        footer && (footer.style.marginLeft = null);
        gridContainer && gridContainer.forEach(gridItem => {
            gridItem.removeAttribute("style");
        });
    };

    const copyTopNavigation = () => {
        if (!topNav) {
            const navigationItems = document.querySelector(".header__utility__nav");
            if (!navigationItems) return;
            const navigationItemsClone = navigationItems.cloneNode(true);
            // basket
            const basketButton = navigationItemsClone.querySelector(".white-basket-full");
            const basketImage = document.createElement("img");
            const basketCircle = basketButton.querySelector(".header__basket__circle");
            const basketCount = basketCircle.querySelector(".header__basket__number");
            // m59 dropdown
            const m59Dropdown = navigationItemsClone.querySelector(".m59");
            const m59Button = m59Dropdown.querySelector(".m59__trigger");
            const m59DropdownList = m59Dropdown.querySelector(".m59__dropdown");
            // search button
            const searchImage = navigationItemsClone.querySelector(".header__utility-search img");
    
            navigationItemsClone.removeAttribute("class");
            navigationItemsClone.querySelector(".header__basket").appendChild(navigationItemsClone.querySelector(".m58"));
    
            basketImage.src = basketIcon;
            basketButton.appendChild(basketImage);
            basketButton.setAttribute("class", "submenu-dropdown basket js-open-basket copy-nav");
            basketButton.setAttribute("aria-expanded", "false");
            basketCircle.removeAttribute("class");
            basketCount.setAttribute("class", "basket-count");
    
            m59Dropdown.setAttribute("class", "user-menu-wrapper");
            m59Button.setAttribute("class", "submenu-dropdown user-menu");
            m59DropdownList.setAttribute("class", "user-menu-list");
    
            searchImage.src = searchIcon;
            searchImage.removeAttribute("width");
            searchImage.removeAttribute("height");
    
            const htmlString = navigationItemsClone.outerHTML;

            setTopNav({ 
                __html: htmlString
            });
        }
    };

    const copyTopMobileDropdowns = () => {
        if (!mobileNav) {
            const basketButton = document.querySelector("header .header__mobile-controls .white-basket-full");
            const basketDropdown = document.querySelector("header .header__mobile-controls .m58");
            const userMenuDropdown = document.querySelector("header .m59__list.user");
            if (!basketButton || !basketDropdown || !userMenuDropdown) return;
            const basketButtonClone = basketButton.cloneNode(true);
            const basketDropdownClone = basketDropdown.cloneNode(true);
            const userMenuDropdownClone = userMenuDropdown.cloneNode(true);
            const basketButtonHtmlString = basketButtonClone.outerHTML;
            const basketHtmlString = basketDropdownClone.outerHTML;
            const userMenuHtmlString = userMenuDropdownClone.outerHTML;
    
            setMobileNav({ 
                basketButton: {
                    __html: basketButtonHtmlString
                },
                basketDropdown: {
                    __html: basketHtmlString
                },
                userMenu: {
                    __html: userMenuHtmlString
                }
            });
        }
    };

    /**
     * Handles input focus
     */
    const handleFocus = event => {
        if (event) event.preventDefault();

        // sets focus to currently focused input
        setFocus(event.target.name);
    };

    /**
     * Handles input blur
     */
    const handleBlur = event => {
        if (event) event.preventDefault();

        // sets focus to null so label animation resets
        setFocus(null);
        // handleSubmit(event);
    };

    /**
     * Handles input keyups
     */
    const handleKeyup = () => {
        // set isSubmitting to false so the we dont do an api
        setIsSubmitting(false);

        // validates inputs on the fly
        if (isValidating) {
            setErrors(validate(values, null, config));
        }
    };

    /**
     * `path` tells use what path the app shoukld be on `calc` or `returns`
     */
    const [path] = useGlobal("path");

    /**
     * get the `returnMethod` if it's set to `collection` or `dropoff`
     */
    const [returnMethod] = useGlobal("returnMethod");

    // returning all varaibles and functions that we can to use inside other components
    return {
        handleChange,
        handleChangePhone,
        handleChangeName,
        handleChangeCheckbox,
        handleChangeRadio,
        handleChangeNoDigits,
        handleKeyup,
        handleSubmit,
        handleProceedToPayment,
        handleFocus,
        handleBlur,
        setIsLoading,
        setNavProfileLinks,
        copyTopMobileDropdowns,
        copyTopNavigation,
        handleMainMargin,
        removeNavMargins,
        isLoading,
        values,
        errors,
        focus,
        hasErrors,
        globalError,
        handleCloseError,
        setFocus,
        isValidating,
		setIsValidating,
        path,
        returnMethod,
        setIndividualValue
    };
};

export default AppHook;
